/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from '../../collapsible-card/jsx/Card';
import Collapsible from '../../collapsible/jsx/Collapsible';
import StatusMessage from '../../status-message/jsx/StatusMessage';
import EmailsListingContext, {
    EmailsListingContextErrorType as ErrorType,
} from './data/emails-listing-context';

/**
 * Loading partial.
 * @return {JSX.Element}
 * @constructor
 */
export function LoadingState() {
    return (
        <>
            <span className="sr-only">Loading...</span>
            <ol className="emails-list">
                <li className="emails-list__item emails-list--loading" />
                <li className="emails-list__item emails-list--loading" />
                <li className="emails-list__item emails-list--loading" />
                <li className="emails-list__item emails-list--loading" />
            </ol>
        </>
    );
}

/**
 * Handle error messages.
 *
 * @param {object} error Error object.
 * @return {JSX.Element|null}
 * @constructor
 */
export function ErrorState({ error, restartAuthProcess }) {
    let errorType = '';
    let errorMessage;

    if (error) {
        if (error === ErrorType.AUTH_ERROR) {
            errorType = 'auth';
            errorMessage =
                'An error has occurred in authenticating your user account.';
        } else if (error === ErrorType.POPUP_ERROR) {
            errorType = 'popup';
            errorMessage =
                'An error has occurred in opening the authentication popup. Ensure popups are allowed and you are not closing the popup window.';
        } else if (error === ErrorType.SCOPE_ERROR) {
            errorType = 'scope';
            errorMessage =
                'An error has occurred loading your emails, ensure you have approved the required permissions when prompted.';
        } else if (error === ErrorType.ERROR) {
            errorType = 'error';
            errorMessage = 'An error has occurred loading your emails.';
        }

        errorMessage = (
            <>
                {errorMessage}
                {errorType !== 'error' && (
                    <button
                        type="button"
                        onClick={() => {
                            restartAuthProcess();
                        }}
                        className="emails-listing__error-retry"
                    >
                        Retry login
                    </button>
                )}
            </>
        );

        return <StatusMessage title={errorMessage} />;
    }
    return null;
}

/**
 * Emails listing.
 *
 * @param {array} emails Array of email objects.
 *
 * @return {JSX.Element}
 * @constructor
 */
function EmailsList({ emails }) {
    if (!emails || !emails.length) {
        return <StatusMessage title="Your inbox is empty" icon="email" />;
    }

    const titlePartial = ({ email }) => {
        const wrapperClasses = [
            'email-heading',
            email.isRead ? 'email-heading--not-read' : '',
        ].join(' ');

        return (
            <div className={wrapperClasses}>
                <div className="email-heading__details">
                    <div className="email-heading__from">{email.from}</div>
                    <div className="email-heading__title">{email.title}</div>
                </div>
                {email.link && (
                    <a
                        className="email-heading__link"
                        href={email.link}
                        rel="noreferrer"
                        target="_blank"
                    >
                        <svg className="svg-icon">
                            <use href="#external" />
                            <title>External icon</title>
                        </svg>
                        <span className="sr-only">Link to email content</span>
                    </a>
                )}
            </div>
        );
    };

    const collapsedTitle = (subtitle) => (
        <div className="email-collapsible__subtitle">{subtitle}</div>
    );

    return (
        <ol className="emails-list no-wysiwyg">
            {emails.map((email) => (
                <li key={`email-${email.id}`} className="emails-list__item">
                    {titlePartial({ email })}

                    <div className="email-subtitle">{email.subtitle}</div>

                    <div className="email-collapsible">
                        <Collapsible
                            displayChevron
                            collapsedTitle={collapsedTitle(email.subtitle)}
                            expandedTitle={email.title}
                            isOpen={false}
                            additionalButtonClasses="email-collapsible__show-more"
                            additionalContentClasses="email-collapsible__content"
                        >
                            {email.subtitle}
                        </Collapsible>
                    </div>
                </li>
            ))}
        </ol>
    );
}

/**
 * Email listing entry point.
 *
 * @param {string} title Box title.
 * @param {string} heading Heading above listing.
 * @param {number} emailCount Number of emails to fetch.
 *
 * @return {JSX.Element}
 * @constructor
 */
export default function EmailsListing({ title, heading, emailCount }) {
    const {
        isLoading,
        error,
        isReady,
        emails,
        getEmails,
        isAuthNeeded,
        signInLink,
        restartAuthProcess,
        mailboxLink,
    } = useContext(EmailsListingContext);

    useEffect(() => isReady && getEmails({ count: emailCount }), [isReady]);

    return (
        <Card
            isCollapsible
            title={title}
            contentClasses="emails-listing"
            id="emails-listing"
        >
            <h2 className="emails-listing__heading">{heading}</h2>
            {isAuthNeeded && (
                <StatusMessage
                    title="No emails available"
                    icon="email"
                    className="emails-listing-sign-in"
                >
                    <div className="emails-listing-sign-in__message">
                        {signInLink}
                    </div>
                </StatusMessage>
            )}
            {isLoading && !isAuthNeeded && !error && <LoadingState />}
            {error && (
                <ErrorState
                    error={error}
                    restartAuthProcess={restartAuthProcess}
                />
            )}
            {isReady && !isLoading && !isAuthNeeded && !error && (
                <EmailsList emails={emails} />
            )}
            <a
                className="emails-listing__footer-button"
                href={mailboxLink}
                target="_blank"
                rel="noreferrer"
            >
                <svg className="svg-icon">
                    <use href="#external" />
                    <title>External icon</title>
                </svg>
                Check your inbox
            </a>
        </Card>
    );
}

const { string, number } = PropTypes;

EmailsListing.propTypes = {
    title: string,
    heading: string,
    emailCount: number,
};

EmailsListing.defaultProps = {
    title: 'Emails',
    heading: 'Recent Emails',
    emailCount: 10,
};
